import { useCallback, useState } from "react";
import {
  calculateMaxTokensForPlayer,
  canDistributeMore,
  createInitialPlayerTokens,
  type DistributionSummary,
  generateRandomTokenDistribution,
  getDistributionSummary,
  getTokensForPlayer,
  getTokensRemaining,
  getTotalTokens,
  isDistributionComplete,
  type PlayerTokens,
  resetAllTokens,
} from "./form-calculations.ts";
import {
  applyRandomDistribution,
  trySetTokensForPlayer,
  type ValidationError,
} from "./form-operations.ts";
import { validateHookOptions, validators } from "./form-validation.ts";

type UsePowerupPlayersFormOptions = {
  playerIds: string[];
  maxTokens: number;
  maxTokensPerPlayer: number;
  onError?: (error: ValidationError) => void;
};

const PLAYER_SELECTION_COUNT = 3;

export function usePowerupPlayersForm({
  playerIds,
  maxTokens,
  maxTokensPerPlayer,
  onError,
}: UsePowerupPlayersFormOptions) {
  // Validate hook options at initialization
  const validation = validateHookOptions({
    playerIds,
    maxTokens,
    maxTokensPerPlayer,
    expectedPlayerCount: PLAYER_SELECTION_COUNT,
  });

  if (!validation.isValid) {
    throw new Error(validation.errors.join("; "));
  }

  // Initialize state with zero tokens for all players
  const [playerTokens, setPlayerTokens] = useState<PlayerTokens[]>(() =>
    createInitialPlayerTokens(playerIds),
  );

  // Error handling callback
  const handleError = useCallback(
    (error: ValidationError) => {
      if (onError) {
        onError(error);
      } else {
        const errorMessage = `${error.type}: ${error.message}`;
        console.warn(errorMessage);
      }
    },
    [onError],
  );

  // Derived state calculations
  const totalTokens = getTotalTokens(playerTokens);
  const tokensRemaining = getTokensRemaining(playerTokens, maxTokens);
  const isFullyDistributed = isDistributionComplete(playerTokens, maxTokens);
  const isValid = isFullyDistributed;
  const canDistributeMoreTokens = canDistributeMore(playerTokens, maxTokens);

  // Player token operations
  const getTokensForPlayerById = useCallback(
    (id: string): number => {
      if (!validators.isValidPlayerId(id)) {
        handleError({
          type: "INVALID_INPUT",
          message: "Player ID must be a non-empty string",
          playerId: id,
        });
        return 0;
      }

      return getTokensForPlayer(id, playerTokens);
    },
    [playerTokens, handleError],
  );

  const getMaxTokensForPlayer = useCallback(
    (id: string): number => {
      if (!validators.isValidPlayerId(id)) {
        handleError({
          type: "INVALID_INPUT",
          message: "Player ID must be a non-empty string",
          playerId: id,
        });
        return 0;
      }

      return calculateMaxTokensForPlayer(
        id,
        playerTokens,
        maxTokens,
        maxTokensPerPlayer,
      );
    },
    [playerTokens, maxTokens, maxTokensPerPlayer, handleError],
  );

  const setTokensForPlayer = useCallback(
    (id: string, newTokens: number): boolean => {
      let success = true;

      setPlayerTokens((currentTokens) => {
        const result = trySetTokensForPlayer(
          id,
          newTokens,
          currentTokens,
          maxTokens,
          maxTokensPerPlayer,
        );

        if (!result.success) {
          if (result.error) {
            success = false;
            handleError(result.error);
          }
          return currentTokens;
        }

        if (result.updatedPlayers) {
          return result.updatedPlayers;
        }

        return currentTokens;
      });

      return success;
    },
    [maxTokens, maxTokensPerPlayer, handleError],
  );

  // Bulk operations
  const resetTokens = useCallback(() => {
    const resetPlayers = resetAllTokens(playerTokens);
    setPlayerTokens(resetPlayers);
  }, [playerTokens]);

  const randomizeTokens = useCallback(() => {
    const distribution = generateRandomTokenDistribution(
      playerTokens.length,
      maxTokens,
      maxTokensPerPlayer,
    );

    const result = applyRandomDistribution(playerTokens, distribution);

    if (!result.success) {
      if (result.error) {
        handleError(result.error);
      }
      return;
    }

    if (result.updatedPlayers) {
      setPlayerTokens(result.updatedPlayers);
    }
  }, [playerTokens, maxTokens, maxTokensPerPlayer, handleError]);

  // Utility functions
  const getDistributionSummaryData = useCallback((): DistributionSummary => {
    return getDistributionSummary(playerTokens);
  }, [playerTokens]);

  return {
    // State
    totalTokens,
    tokensRemaining,
    playerTokens,

    // Validation
    isValid,
    isFullyDistributed,
    canDistributeMore: canDistributeMoreTokens,

    // Player operations
    getTokensForPlayer: getTokensForPlayerById,
    getMaxTokensForPlayer,
    setTokensForPlayer,

    // Bulk operations
    resetTokens,
    randomizeTokens,

    // Utilities
    getDistributionSummary: getDistributionSummaryData,
  };
}
